import { useState } from 'react';
import { Plus, Trash2, GripVertical, Star, Check, X } from 'lucide-react';
import {
  useTaskStatuses,
  useCreateTaskStatus,
  useUpdateTaskStatusDef,
  useReorderTaskStatuses,
  useDeleteTaskStatus,
} from '../../hooks/useTaskStatuses';
import type { TaskStatusCategory, TaskStatusDef } from '../../lib/taskStatusesApi';
import { Select } from '../ui/Select';
import { useConfirm } from '../ui/ConfirmProvider';
import { useI18n } from '../../contexts/I18nContext';

// Settings → "Statusuri sarcini" (CLE-DASH).
//
// Configurable task statuses backing the Kanban board and the task list. One
// draggable list à la the leads StageManager: reorder, inline edit (name,
// color, category), set the default status, delete with task reassignment.
// Client-side invariants mirror the backend: at least one open (non-terminal)
// status, at least one COMPLETED status, and the default stays non-terminal.

const COLOR_SWATCHES = [
  '#3b82f6',
  '#eab308',
  '#22c55e',
  '#16a34a',
  '#a855f7',
  '#f97316',
  '#ef4444',
  '#64748b',
];

const DEFAULT_COLOR = '#3b82f6';

const CATEGORIES: TaskStatusCategory[] = [
  'BACKLOG',
  'UNSTARTED',
  'STARTED',
  'COMPLETED',
  'CANCELLED',
];

function isTerminal(category: TaskStatusCategory): boolean {
  return category === 'COMPLETED' || category === 'CANCELLED';
}

/** Mirror of the backend invariants: ≥1 open status and ≥1 COMPLETED status. */
function invariantViolation(statuses: TaskStatusDef[]): string | null {
  if (!statuses.some((s) => !isTerminal(s.category)))
    return 'Trebuie să existe cel puțin un status deschis (ne-terminal).';
  if (!statuses.some((s) => s.category === 'COMPLETED'))
    return 'Trebuie să existe cel puțin un status de tip Finalizat.';
  const def = statuses.find((s) => s.isDefault);
  if (def && isTerminal(def.category))
    return 'Statusul implicit trebuie să fie unul deschis (ne-terminal).';
  return null;
}

export function TaskStatusesSettings() {
  const { statuses, isLoading, isError, error } = useTaskStatuses();
  const reorder = useReorderTaskStatuses();
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const invariant = invariantViolation(statuses);

  function handleDrop(targetId: string) {
    const sourceId = dragId;
    setDragId(null);
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;

    const ids = statuses.map((s) => s.id);
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(from, 1);
    ids.splice(to, 0, sourceId);

    setListError(null);
    reorder.mutate(ids, {
      onError: (err) =>
        setListError(err instanceof Error ? err.message : 'Statusurile nu au putut fi reordonate.'),
    });
  }

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-gray-500">
        Configurați statusurile sarcinilor. Ordinea de aici determină coloanele de pe panoul Kanban
        și gruparea din listă — modificările se reflectă imediat. Trebuie să rămână cel puțin un
        status deschis și unul de tip Finalizat.
      </p>

      {isLoading && <p className="mt-6 text-sm text-gray-500">Se încarcă statusurile...</p>}
      {isError && (
        <p className="mt-6 text-sm text-red-600">
          Statusurile nu au putut fi încărcate
          {error instanceof Error ? `: ${error.message}` : ''}.
        </p>
      )}

      {!isLoading && !isError && (
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Statusuri</h3>
            <span className="text-xs text-gray-400">Trageți pentru a reordona</span>
          </div>

          {invariant && (
            <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-700">
              {invariant}
            </p>
          )}
          {listError && (
            <p className="mt-2 rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-red-600">
              {listError}
            </p>
          )}

          <ul className="mt-3 flex flex-col gap-2">
            {statuses.map((status) => (
              <li
                key={status.id}
                draggable
                onDragStart={(e) => {
                  setDragId(status.id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOverId(status.id);
                }}
                onDrop={() => handleDrop(status.id)}
                className={`rounded-md border ${
                  overId === status.id && dragId && dragId !== status.id
                    ? 'border-brand-400 ring-1 ring-brand-300'
                    : 'border-gray-200'
                } ${dragId === status.id ? 'opacity-50' : ''}`}
              >
                <StatusRow status={status} statuses={statuses} />
              </li>
            ))}
          </ul>

          <AddStatusRow />
        </div>
      )}
    </div>
  );
}

// --- Single status row --------------------------------------------------------

function StatusRow({ status, statuses }: { status: TaskStatusDef; statuses: TaskStatusDef[] }) {
  const { t } = useI18n();
  const updateStatus = useUpdateTaskStatusDef();
  const deleteStatus = useDeleteTaskStatus();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [name, setName] = useState(status.name);
  const [color, setColor] = useState(status.color);
  const [category, setCategory] = useState<TaskStatusCategory>(status.category);
  const [error, setError] = useState<string | null>(null);

  const categoryOptions = CATEGORIES.map((c) => ({
    value: c,
    label: t(`tasks.statusCategory.${c}`),
  }));

  function projectedCategoryViolation(nextCategory: TaskStatusCategory): string | null {
    const projected = statuses.map((s) =>
      s.id === status.id ? { ...s, category: nextCategory } : s,
    );
    return invariantViolation(projected);
  }

  function deleteWouldViolate(): string | null {
    if (status.isDefault) return 'Statusul implicit nu poate fi șters.';
    return invariantViolation(statuses.filter((s) => s.id !== status.id));
  }

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Numele este obligatoriu.');
      return;
    }
    const violation = projectedCategoryViolation(category);
    if (violation) {
      setError(violation);
      return;
    }
    setError(null);
    try {
      await updateStatus.mutateAsync({
        id: status.id,
        input: { name: trimmed, color, category },
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Statusul nu a putut fi salvat.');
    }
  }

  async function handleSetDefault() {
    if (status.isDefault) return;
    if (isTerminal(status.category)) {
      setError('Statusul implicit trebuie să fie unul deschis (ne-terminal).');
      return;
    }
    setError(null);
    try {
      await updateStatus.mutateAsync({ id: status.id, input: { isDefault: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Statusul implicit nu a putut fi setat.');
    }
  }

  async function startDelete() {
    const violation = deleteWouldViolate();
    if (violation) {
      setError(violation);
      return;
    }
    setError(null);
    if (status.taskCount > 0) {
      setReassignTo('');
      setConfirmingDelete(true);
      return;
    }
    if (!(await confirm({ message: `Ștergeți statusul "${status.name}"?`, danger: true }))) return;
    void doDelete(undefined);
  }

  async function doDelete(target: string | undefined) {
    try {
      await deleteStatus.mutateAsync({ id: status.id, reassignTo: target });
      setConfirmingDelete(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Statusul nu a putut fi șters.');
    }
  }

  if (confirmingDelete) {
    const targets = statuses
      .filter((s) => s.id !== status.id)
      .map((s) => ({ value: s.id, label: s.name, color: s.color }));
    return (
      <div className="p-3">
        <p className="text-sm text-gray-700">
          Statusul <span className="font-medium">{status.name}</span> are {status.taskCount}{' '}
          {status.taskCount === 1 ? 'sarcină' : 'sarcini'}. Alegeți statusul în care vor fi mutate:
        </p>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div className="w-56">
            <Select
              label="Mută sarcinile în"
              hideLabel
              value={reassignTo}
              onChange={setReassignTo}
              options={targets}
              placeholder="Alege status..."
              size="sm"
            />
          </div>
          <button
            type="button"
            onClick={() => doDelete(reassignTo)}
            disabled={!reassignTo || deleteStatus.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Trash2 size={14} /> {deleteStatus.isPending ? 'Se șterge...' : 'Șterge și mută'}
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirmingDelete(false);
              setError(null);
            }}
            className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Anulează
          </button>
        </div>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  if (editing) {
    return (
      <div className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-8 w-9 cursor-pointer rounded border border-gray-300"
            aria-label="Culoare status"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="min-w-[140px] flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            placeholder="Nume status"
          />
          <div className="w-40">
            <Select
              label="Categorie"
              hideLabel
              value={category}
              onChange={(v) => setCategory(v as TaskStatusCategory)}
              options={categoryOptions}
              size="sm"
            />
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={updateStatus.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
          >
            <Check size={14} /> Salvează
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setError(null);
              setName(status.name);
              setColor(status.color);
              setCategory(status.category);
            }}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <X size={14} /> Anulează
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {COLOR_SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              style={{ backgroundColor: c }}
              className={`h-5 w-5 rounded-full ${
                color.toLowerCase() === c.toLowerCase() ? 'ring-2 ring-gray-400 ring-offset-1' : ''
              }`}
              aria-label={`Folosește culoarea ${c}`}
            />
          ))}
        </div>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    // Phones: name, category and count fill the first line, the three actions
    // sit right-aligned on a second one (all in one row the name was cut to
    // a letter and "Editează" wrapped under it on its own).
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 p-2.5">
      <div className="flex min-w-0 basis-full items-center gap-3 sm:flex-1 sm:basis-auto">
        <GripVertical size={16} className="shrink-0 cursor-grab text-gray-300" />
        <span
          className="h-3.5 w-3.5 shrink-0 rounded-full"
          style={{ backgroundColor: status.color }}
        />
        <span className="flex-1 truncate text-sm font-medium text-gray-800">{status.name}</span>
        <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
          {t(`tasks.statusCategory.${status.category}`)}
        </span>
        <span className="shrink-0 text-xs text-gray-400">
          {status.taskCount} {status.taskCount === 1 ? 'sarcină' : 'sarcini'}
        </span>
      </div>
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          onClick={handleSetDefault}
          disabled={updateStatus.isPending}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-60"
          aria-label={status.isDefault ? 'Status implicit' : 'Setează ca status implicit'}
          title={status.isDefault ? 'Status implicit' : 'Setează ca status implicit'}
        >
          <Star size={15} className={status.isDefault ? 'fill-amber-500 text-amber-500' : ''} />
        </button>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
        >
          Editează
        </button>
        <button
          type="button"
          onClick={startDelete}
          disabled={deleteStatus.isPending}
          className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-60"
          aria-label="Șterge status"
        >
          <Trash2 size={15} />
        </button>
      </div>
      {error && (
        <span className="basis-full pt-1 text-sm text-red-600" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

// --- Add status ----------------------------------------------------------------

function AddStatusRow() {
  const { t } = useI18n();
  const createStatus = useCreateTaskStatus();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [category, setCategory] = useState<TaskStatusCategory>('UNSTARTED');
  const [error, setError] = useState<string | null>(null);

  const categoryOptions = CATEGORIES.map((c) => ({
    value: c,
    label: t(`tasks.statusCategory.${c}`),
  }));

  async function handleAdd() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    try {
      await createStatus.mutateAsync({ name: trimmed, color, category });
      setName('');
      setColor(DEFAULT_COLOR);
      setCategory('UNSTARTED');
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Statusul nu a putut fi adăugat.');
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
      >
        <Plus size={15} /> Adaugă status
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-gray-200 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          className="h-8 w-9 cursor-pointer rounded border border-gray-300"
          aria-label="Culoare status"
        />
        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nume status"
          className="min-w-[140px] flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
        <div className="w-40">
          <Select
            label="Categorie"
            hideLabel
            value={category}
            onChange={(v) => setCategory(v as TaskStatusCategory)}
            options={categoryOptions}
            size="sm"
          />
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!name.trim() || createStatus.isPending}
          className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {createStatus.isPending ? 'Se adaugă...' : 'Adaugă'}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setError(null);
            setName('');
          }}
          className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Anulează
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {COLOR_SWATCHES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setColor(c)}
            style={{ backgroundColor: c }}
            className={`h-5 w-5 rounded-full ${
              color.toLowerCase() === c.toLowerCase() ? 'ring-2 ring-gray-400 ring-offset-1' : ''
            }`}
            aria-label={`Folosește culoarea ${c}`}
          />
        ))}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
